import { createAction as makeActionCreator } from 'redux-actions';
import { FinnishSSN } from 'finnish-ssn';

import api from '../api/';
import { loadRoot, fetchPathAfterLogin, verifyEmployerForSelectedPath, closePromptEmployerSelection } from './booking';
import { fetchUponLogin } from './ehrResources';

export const checkUserFailure = makeActionCreator('CHECK_USER_FAILURE');
export const identifyUserStart = makeActionCreator('IDENTIFY_USER_START');
export const identifyUserSuccess = makeActionCreator('IDENTIFY_USER_SUCCESS');
export const identifyUserFailure = makeActionCreator('IDENTIFY_USER_FAILURE');
export const identifyUserFailureAccessNotAllowedByEmployer = makeActionCreator('IDENTIFY_USER_FAILURE_ACCESS_NOT_ALLOWED_BY_EMPLOYER');
export const identifyUserFailureAccessNotAllowedByEmployerUserNoted = makeActionCreator('IDENTIFY_USER_FAILURE_ACCESS_NOT_ALLOWED_BY_EMPLOYER_USER_NOTED');
export const selectEmployerDepartment = makeActionCreator('SELECT_EMPLOYER_DEPARTMENT');
export const confirmContinue = makeActionCreator('CONFIRM_CONTINUE');
export const clearSessionSuccess = makeActionCreator('CLEAR_SESSION_SUCCESS');
export const restoreInitialState = makeActionCreator('RESTORE_INITIAL_STATE');
export const sessionTimeOut = makeActionCreator('SESSION_TIMEOUT');
export const setFlagCreateUserIfNotExists = makeActionCreator('SET_USER_FLAG_CREATE_IF_MOT_EXISTS');
export const setFlagRequiresEmployer = makeActionCreator('SET_USER_FLAG_REQUIRES_EMPLOYER');
export const setUserPreRegistrationData = makeActionCreator('USER_SHOULD_REGISTER');
export const registrationUserStart = makeActionCreator('REGISTRATION_USER_START');
export const registrationUserSuccess = makeActionCreator('REGISTRATION_USER_SUCCESS');
export const registrationUserFailure = makeActionCreator('REGISTRATION_USER_FAILURE');
export const identityUserFailureDueToMultipleEmployers = makeActionCreator('LOGIN_FAILED_DUE_TO_MULTIPLE_EMPLOYERS');
export const selectMainEmployer = makeActionCreator('SELECT_MAIN_EMPLOYER');
export const selectEmployer = makeActionCreator('SELECT_EMPLOYER');
export const bankAuthenticationStarted = makeActionCreator('BANK_AUTHENTICATION_ONGOING');
export const bankAuthenticationLinkReceived = makeActionCreator('BANK_AUTHENTICATION_LINK_RECEIVED');

export const setSelectEmployerDepartment = dispatch => (payload) => {
  dispatch(selectEmployerDepartment(payload));
};

export const setSelectMainEmployer = dispatch => (user) => {
  dispatch(selectMainEmployer(user));
};

const identifyUserErrorHandler = dispatch => (error) => {
  // otherwise continue with the error message
  let errorMessage = 'loginFailedGenericMessage';
  if (error.response && error.response.status) {
    if (error.response.status === 402) {
      errorMessage = 'loginFailedMessage-402';
    } else if (error.response.status === 403) {
      // Unauthorized error messages
      if (error.response.data.statusText === 'USER_WITH_SOTU_NOT_FOUND') {
        errorMessage = 'loginFailedUserWithSotuNotFound';
      } else if (error.response.data.statusText === 'MULTIPLE_USERS_WITH_SOTU') {
        errorMessage = 'loginFailedMultipleUsersWithSotu';
      } else if (error.response.data.statusText === 'PATH_REQUIRES_EMPLOYER_BUT_EMPLOYER_DOES_NOT_ALLOW_USER_ACCESS') {
        return dispatch(identifyUserFailureAccessNotAllowedByEmployer(error.response.data.data.localizationKey)); // Message will be taken from organization localizations
      } else if (error.response.data.statusText === 'ORGANIZATION_DOES_NOT_ALLOW_CLIENT_WITHOUT_EMPLOYER') {
        errorMessage = 'loginFailedOrganizationDoesNotAllowClientWithoutEmployer';
      } else if (error.response.data.statusText === 'USER_WITHOUT_CREDIT_CHECK') {
        errorMessage = 'loginFailedUserWithoutCreditCheck';
      } else if (error.response.data.statusText === 'PATH_REQUIRES_EMPLOYER_BUT_NOT_FOUND') {
        errorMessage = 'loginFailedPathRequiresEmployerButNotFound';
      } else if (error.response.data.statusText === 'USER_WITH_SOTU_NOT_FOUND_AND_FAKE_SOTU') {
        errorMessage = 'loginFailedAndRegisterProhibited';
      }
    }
  }

  return dispatch(identifyUserFailure(errorMessage));
};

export const setIdentityUserFailureDueToMultipleEmployers = dispatch => () => dispatch(identityUserFailureDueToMultipleEmployers());

export const setCreateUserIfNotExists = dispatch => (flagCreateUserIfNotExists) => {
  dispatch(setFlagCreateUserIfNotExists(!!flagCreateUserIfNotExists));
};

export const setRequiresEmployer = dispatch => (flagRequiresEmployer) => {
  dispatch(setFlagRequiresEmployer(!!flagRequiresEmployer));
};

export const setSelectEmployer = dispatch => (selectedEmployer) => {
  dispatch(selectEmployer(selectedEmployer));
  dispatch(closePromptEmployerSelection());
  dispatch(fetchPathAfterLogin)();
};

export const identifyUser = dispatch => (formData, afterSuccess) => {
  const { createUserIfNotExists } = formData;
  dispatch(identifyUserStart());
  api.identifyUser(formData)
  .then(data => dispatch(identifyUserSuccess(data)))
  .then(() => dispatch(fetchUponLogin)())
  .then(() => dispatch(verifyEmployerForSelectedPath)())
  .then(() => dispatch(fetchPathAfterLogin)())
  .then(() => {
    if (typeof afterSuccess === 'function') {
      afterSuccess();
    }
  })
  .catch((error) => {
    // verify if the user can be created
    console.log('error in identifyUser: ', error);
    const notFound = error.response && error.response.data &&
      (error.response.data.statusText === ('USER_WITH_SOTU_NOT_FOUND') || error.response.status === 403);
    if (createUserIfNotExists && notFound) {
      if (FinnishSSN.validate(formData.personId) || (!FinnishSSN.validate(formData.personId) && formData.relaxedSotuVerificationEnabled)) {
        return dispatch(setUserPreRegistrationData(formData));
      }
    }
    return dispatch(identifyUserErrorHandler)(error);
  });
};

export const registerUser = dispatch => (formData, afterSuccess) => {
  dispatch(registrationUserStart());
  api.registerUser(formData)
  .then((registrationData) => {
    dispatch(registrationUserSuccess());
    dispatch(identifyUserStart());
    const identifyingData = formData;
    identifyingData.personId = registrationData.sotu;  // Take the temporary SOTU created in Acute
    api.identifyUser(identifyingData)
    .then(data => dispatch(identifyUserSuccess(data)))
    .then((data) => {
      if (typeof afterSuccess === 'function') {
        afterSuccess(data);
      }
    })
    .catch((error) => { dispatch(identifyUserErrorHandler)(error) });
  })
  .catch((error) => { dispatch(registrationUserFailure) });
};

export const updateUser = dispatch => (formData, afterSuccess) => {
  api.updateUser(formData)
    .then(data => dispatch(identifyUserSuccess(data)))
    .then((data) => {
      if (typeof afterSuccess === 'function') {
        afterSuccess(data);
      }
    })
    .catch((error) => { dispatch(registrationUserFailure) });
};

export const clearSession = dispatch => (shouldCloseWindow) => {
  api.clearSession()
  .then((data) => {
    dispatch(clearSessionSuccess(data))
      .then(() => dispatch(restoreInitialState()))
      .then(() => { dispatch(loadRoot)() })
      .then(() => {
        if (shouldCloseWindow) {
          window.close();
        }
      });
  })
  .catch((error) => {
    // console.log(error.response.status);
    // session timeout
    Promise.resolve(true)
      .then(() => dispatch(clearSessionSuccess()))
      .then(() => dispatch(restoreInitialState()))
      // .then(() => dispatch(sessionTimeOut()))
      .then(() => { dispatch(loadRoot)() });
  });
};

export const checkSession = dispatch => () => {
  api.checkSession()
  .catch((error) => {
    // Clear data.
    dispatch(clearSession)();
    // Show a message that session is expired.
  });
};

export const restoreApp = dispatch => () => {
  dispatch(restoreInitialState());
};



// WEBPACK FOOTER //
// ./src/actions/session.js